import { FrontendTenantConfig } from './types';

/**
 * Phase 5 (D6, `react-no-effect`) — plain imperative DOM at boot, no component, no `useEffect`.
 * Tenant state resolves at module-evaluation time (D6), so there is nothing to react to; this runs
 * once, synchronously, from `src/index.tsx`, before `root.render`, and only for a successfully
 * resolved tenant — an unknown hostname / failed D7 validation renders `<TenantNotFound/>` instead
 * and never calls this (see `./index.ts`'s `tenantError`).
 *
 * Sets the resolved tenant's title, description, favicons and manifest. The per-tenant binaries live
 * at `public/tenants/<slug>/**` (phase 5, §4A.2/§4A.9).
 *
 * The icon `<link>`s are **created here, not edited in place**: `public/index.html` deliberately
 * ships no favicon/apple-touch-icon tags, because any base href would either default to one
 * tenant's brand or 404 on every page load now that the base PNGs have moved under `tenants/`.
 * `upsertLink` therefore has to handle the absent case — a `querySelector`-and-only-update version
 * would silently leave every tenant with no icon at all.
 *
 * §4A.6 — the one approved deviation from verbatim porting: WCS's own `manifest.json` misspells
 * its name ("West cost surrogacy"); `public/tenants/wcs/manifest.json` ships the corrected
 * "West Coast Surrogacy" instead, per the human's approval at the Plan gate.
 */

/** Find a `<link>` by its full selector, or create and append it, then set `href`. */
const upsertLink = (selector: string, attributes: Record<string, string>, href: string): void => {
  let link = document.querySelector(selector);

  if (!link) {
    link = document.createElement('link');
    Object.entries(attributes).forEach(([name, value]) => link?.setAttribute(name, value));
    document.head.appendChild(link);
  }

  link.setAttribute('href', href);
};

export const applyDocumentHead = (tenant: FrontendTenantConfig): void => {
  document.title = tenant.head.title;

  const description = document.querySelector('meta[name="description"]');
  if (description) {
    description.setAttribute('content', tenant.head.description);
  }

  const base = `${process.env.PUBLIC_URL}/tenants/${tenant.slug}`;

  upsertLink(
    'link[rel="icon"][sizes="32x32"]',
    { rel: 'icon', type: 'image/png', sizes: '32x32' },
    `${base}/favicon-32x32.png`,
  );
  upsertLink(
    'link[rel="icon"][sizes="16x16"]',
    { rel: 'icon', type: 'image/png', sizes: '16x16' },
    `${base}/favicon-16x16.png`,
  );
  upsertLink('link[rel="apple-touch-icon"]', { rel: 'apple-touch-icon' }, `${base}/logo192.png`);
  upsertLink('link[rel="manifest"]', { rel: 'manifest' }, `${base}/manifest.json`);
};
